import React, { createContext, useContext, useState, ReactNode } from 'react';
import SuccessView, { SuccessViewProps, successVariations } from '../components/common/SuccessView';

interface SuccessContextType {
  showSuccess: (config: SuccessConfig) => void;
  hideSuccess: () => void;
  triggerSuccess: (variationType: keyof typeof successVariations, customConfig?: Partial<SuccessConfig>) => void;
}

interface SuccessConfig extends Omit<SuccessViewProps, 'onComplete'> {
  onComplete?: () => void;
}

interface SuccessProviderProps {
  children: ReactNode;
}

const SuccessContext = createContext<SuccessContextType | undefined>(undefined);

export const useSuccess = (): SuccessContextType => {
  const context = useContext(SuccessContext);
  if (!context) {
    throw new Error('useSuccess must be used within a SuccessProvider');
  }
  return context;
};

export const SuccessProvider: React.FC<SuccessProviderProps> = ({ children }) => {
  const [successConfig, setSuccessConfig] = useState<SuccessConfig | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const showSuccess = (config: SuccessConfig) => {
    setSuccessConfig({
      ...config,
      onComplete: () => {
        hideSuccess();
        config.onComplete?.();
      }
    });
    setIsVisible(true);
  };

  const hideSuccess = () => {
    setIsVisible(false);
    setSuccessConfig(null);
  };

  const triggerSuccess = (variationType: keyof typeof successVariations, customConfig?: Partial<SuccessConfig>) => {
    const variation = successVariations[variationType];
    const finalConfig: SuccessConfig = {
      ...variation,
      ...customConfig,
      confettiConfig: {
        ...variation.confettiConfig,
        ...customConfig?.confettiConfig
      }
    };
    showSuccess(finalConfig);
  };

  const contextValue: SuccessContextType = {
    showSuccess,
    hideSuccess,
    triggerSuccess
  };

  return (
    <SuccessContext.Provider value={contextValue}>
      {children}
      {isVisible && successConfig && (
        <SuccessView
          {...successConfig}
        />
      )}
    </SuccessContext.Provider>
  );
};

// Higher-order component for easy integration
export const withSuccess = <P extends object>(Component: React.ComponentType<P>) => {
  const WrappedComponent = (props: P) => (
    <SuccessProvider>
      <Component {...props} />
    </SuccessProvider>
  );
  
  WrappedComponent.displayName = `withSuccess(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

export default SuccessProvider;